// resources/js/Pages/Formula/questions/questionHooks.js

import { useState, useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import { updateVariableReferences, validateVariableName } from './questionUtils';

/**
 * Custom hook for managing questions state
 */
export function useQuestions(initialQuestions = [], onPromptUpdate = null) {
  const [questions, setQuestions] = useState(initialQuestions);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);

  // Get existing variable names for validation
  const getExistingVariables = useCallback((excludeIndex = null) => {
    return questions
      .filter((_, index) => index !== excludeIndex)
      .map(q => q.variable_name);
  }, [questions]);

  // Add new question
  const addQuestion = useCallback((questionData) => {
    const newQuestion = {
      id: uuid(),
      order: questions.length,
      ...questionData
    };
    
    setQuestions(prev => [...prev, newQuestion]);
    setModalOpen(false);
  }, [questions.length]);

  // Update existing question
  const updateQuestion = useCallback((index, questionData, promptText = '') => {
    const oldQuestion = questions[index];
    const updatedQuestion = { ...oldQuestion, ...questionData };
    
    // Handle variable name change
    let updatedPrompt = promptText;
    let changeCount = 0;
    
    if (oldQuestion.variable_name !== updatedQuestion.variable_name && onPromptUpdate) {
      const result = updateVariableReferences(
        promptText, 
        oldQuestion.variable_name, 
        updatedQuestion.variable_name
      );
      updatedPrompt = result.updatedPrompt;
      changeCount = result.changeCount;
      
      // Notify parent about prompt change
      onPromptUpdate(updatedPrompt, changeCount, oldQuestion.variable_name, updatedQuestion.variable_name);
    }
    
    setQuestions(prev => prev.map((q, i) => i === index ? updatedQuestion : q));
    setModalOpen(false);
    setEditingQuestion(null);
    setEditingIndex(null);
    
    return { updatedPrompt, changeCount };
  }, [questions, onPromptUpdate]);

  // Delete question
  const deleteQuestion = useCallback((index) => {
    setQuestions(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Reorder questions (for drag & drop)
  const reorderQuestions = useCallback((startIndex, endIndex) => {
    setQuestions(prev => {
      const result = Array.from(prev);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);
      
      // Update order property
      return result.map((question, index) => ({
        ...question,
        order: index
      }));
    });
  }, []);

  // Open modal for adding
  const openAddModal = useCallback(() => {
    setEditingQuestion(null);
    setEditingIndex(null);
    setModalOpen(true);
  }, []);

  // Open modal for editing
  const openEditModal = useCallback((question, index) => {
    setEditingQuestion(question);
    setEditingIndex(index);
    setModalOpen(true);
  }, []);

  // Close modal
  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingQuestion(null);
    setEditingIndex(null);
  }, []);

// In the validateQuestion function, add validation for custom dropdown

const validateQuestion = useCallback((questionData, excludeIndex = null) => {
  const errors = {};
  
  if (!questionData.label?.trim()) {
    errors.label = 'Question label is required';
  }
  
  if (!questionData.variable_name?.trim()) {
    errors.variable_name = 'Variable name is required';
  } else {
    const variableError = validateVariableName(
      questionData.variable_name, 
      getExistingVariables(excludeIndex)
    );
    if (variableError) {
      errors.variable_name = variableError;
    }
  }
  
  // NEW: Validate custom dropdown options
  if (questionData.type === 'custom_dropdown') {
    if (!questionData.options || !Array.isArray(questionData.options)) {
      errors.options = 'Dropdown options are required';
    } else {
      const validOptions = questionData.options.filter(opt => 
        opt && opt.label && opt.label.trim() && opt.value && opt.value.trim()
      );
      
      if (validOptions.length === 0) {
        errors.options = 'At least one valid option is required';
      }
      
      // Check for duplicate values
      const values = validOptions.map(opt => opt.value);
      const uniqueValues = [...new Set(values)];
      if (values.length !== uniqueValues.length) {
        errors.options = 'Option values must be unique';
      }
    }
  }
  
  return Object.keys(errors).length > 0 ? errors : null;
}, [getExistingVariables]);

  return {
    questions,
    modalOpen,
    editingQuestion,
    editingIndex,
    addQuestion,
    updateQuestion,
    deleteQuestion,
    reorderQuestions,
    openAddModal,
    openEditModal,
    closeModal,
    validateQuestion,
    getExistingVariables
  };
}

// resources/js/Pages/Formula/questions/questionHooks.js
// Simple version of useVariableInsertion without setTimeout

export function useVariableInsertion(textareaRef, currentValue, onValueChange) {
  const insertVariable = useCallback((variableName) => {
    if (!textareaRef.current) return;
    
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    // ✅ Use the actual textarea value instead of controlled value
    const text = textarea.value;
    const before = text.substring(0, start);
    const after = text.substring(end);
    const newText = before + `{{${variableName}}}` + after;
    
    // Update through callback
    onValueChange?.(newText);
    
    // Move cursor after inserted variable
    setTimeout(() => {
      const newCursorPos = start + `{{${variableName}}}`.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
      textarea.focus();
    }, 0);
  }, [textareaRef, onValueChange]); // ✅ Remove currentValue dependency

  return { insertVariable };
}